import { CSSProperties } from 'react'
import styled, { type DefaultTheme } from 'styled-components'

import type { Spacings } from '@/theme/defaults'

type SpaceKey = keyof Spacings

// Maps each space-separated part to its theme token.
// "16" → "1.6rem", "0 8" → "0rem 0.8rem", "4 8 4 8" → "0.4rem 0.8rem 0.4rem 0.8rem"
const resolveSpacing = (theme: DefaultTheme, value?: SpaceKey | string): string | undefined => {
  if (value === undefined) return undefined
  return String(value)
    .split(' ')
    .map((part) => {
      const key = part === 'auto' ? 'auto' : Number(part)
      return key in theme.space ? theme.space[key as SpaceKey] : part
    })
    .join(' ')
}

export const Flex = styled.div<{
  $direction?: CSSProperties['flexDirection']
  $align?: CSSProperties['alignItems']
  $justify?: CSSProperties['justifyContent']
  $wrap?: CSSProperties['flexWrap']
  $gap?: SpaceKey
  $columnGap?: SpaceKey
  $rowGap?: SpaceKey
  $flex?: CSSProperties['flex']
  $grow?: CSSProperties['flexGrow']
  $shrink?: CSSProperties['flexShrink']
  $basis?: CSSProperties['flexBasis']
  $padding?: SpaceKey | string
  $paddingX?: SpaceKey | string
  $paddingY?: SpaceKey | string
  $margin?: SpaceKey | string
  $marginX?: SpaceKey | string
  $marginY?: SpaceKey | string
}>(
  ({
    theme,
    $direction,
    $align,
    $justify,
    $wrap,
    $gap,
    $columnGap,
    $rowGap,
    $flex,
    $grow,
    $shrink,
    $basis,
    $padding,
    $paddingX,
    $paddingY,
    $margin,
    $marginX,
    $marginY,
  }) => ({
    alignItems: $align,
    columnGap: $columnGap !== undefined ? theme.space[$columnGap] : undefined,
    display: 'flex',
    flex: $flex,
    flexBasis: $basis,
    flexDirection: $direction,
    flexGrow: $grow,
    flexShrink: $shrink,
    flexWrap: $wrap,
    gap: $gap !== undefined ? theme.space[$gap] : undefined,
    justifyContent: $justify,
    margin: resolveSpacing(theme, $margin),
    marginBlock: resolveSpacing(theme, $marginY),
    marginInline: resolveSpacing(theme, $marginX),
    padding: resolveSpacing(theme, $padding),
    paddingBlock: resolveSpacing(theme, $paddingY),
    paddingInline: resolveSpacing(theme, $paddingX),
    rowGap: $rowGap !== undefined ? theme.space[$rowGap] : undefined,
  }),
)
